import React from 'react';
import { MatterFilters } from '../../../types/matters';
import MatterFiltersComponent from './MatterFilters.tsx';

interface MattersControlsBarProps {
  currentView: 'table' | 'card' | 'kanban';
  onViewChange: (view: 'table' | 'card' | 'kanban') => void;
  filters: MatterFilters;
  onFiltersChange: (filters: Partial<MatterFilters>) => void;
  totalCount: number;
  onNewMatter: () => void;
  chatVisible?: boolean;
  isLexPinned?: boolean;
  isSidebarExpanded?: boolean;
  windowWidth?: number;
}

const MattersControlsBar: React.FC<MattersControlsBarProps> = ({
  currentView,
  onViewChange,
  filters,
  onFiltersChange,
  totalCount,
  onNewMatter,
  chatVisible = false,
  isLexPinned = false,
  isSidebarExpanded = true,
  windowWidth = 1024
}) => {
  const viewButtons = [
    {
      id: 'table' as const,
      label: 'Table View',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      )
    },
    {
      id: 'card' as const,
      label: 'Card View',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    },
    {
      id: 'kanban' as const,
      label: 'Kanban View',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0a2 2 0 012 2v6a2 2 0 01-2 2" />
        </svg>
      )
    }
  ];

  // Calculate dynamic spacing based on sidebar and chat states
  const getContainerStyles = () => {
    // Base padding
    let paddingX = 'px-4 sm:px-6';
    
    // Adjust for narrow layouts when chat is pinned and sidebar is expanded
    if (windowWidth >= 768 && chatVisible && isLexPinned) {
      // When both sidebar and chat are visible, reduce padding to prevent overcrowding
      if (isSidebarExpanded) {
        paddingX = 'px-2 sm:px-3';
      } else {
        paddingX = 'px-3 sm:px-4';
      }
    }
    
    return {
      paddingX,
      // Add extra spacing adjustments if needed
      extraClasses: chatVisible && isLexPinned && windowWidth < 1200 ? 'lg:gap-2' : 'lg:gap-4'
    };
  };

  const containerStyles = getContainerStyles();

  return (
    <div className={`${containerStyles.paddingX} py-4 sm:py-6`}>
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className={`px-4 sm:px-6 py-4`}>
          <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 ${containerStyles.extraClasses}`}>
            {/* Left side - Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
              {/* Filters */}
              <div className="flex-shrink-0">
                <MatterFiltersComponent 
                  filters={filters}
                  onChange={onFiltersChange}
                />
              </div>
            </div>

            {/* Right side - View toggles and Add button */}
            <div className={`flex items-center justify-between sm:justify-start gap-2 ${chatVisible && isLexPinned && windowWidth < 1200 ? 'sm:gap-2 lg:gap-3' : 'sm:gap-3 lg:gap-4'} flex-shrink-0`}>
              {/* Results count - hide on very small screens */}
              <div className="hidden sm:block text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                {totalCount} engagement{totalCount !== 1 ? 's' : ''}
              </div>
              
              {/* View toggle buttons */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {viewButtons.map((button) => (
                  <button
                    key={button.id}
                    onClick={() => onViewChange(button.id)}
                    className={`p-2 rounded-md transition-colors ${
                      currentView === button.id
                        ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                    title={button.label}
                  >
                    {button.icon}
                  </button>
                ))}
              </div>

              {/* Add Engagement button */}
              <button
                onClick={onNewMatter}
                className={`${chatVisible && isLexPinned && windowWidth < 1200 ? 'px-2 sm:px-3' : 'px-3 sm:px-4'} py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1 sm:gap-2 h-10 whitespace-nowrap`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className={chatVisible && isLexPinned && windowWidth < 1200 ? "hidden lg:inline" : "hidden sm:inline"}>New Engagement</span>
                <span className={chatVisible && isLexPinned && windowWidth < 1200 ? "lg:hidden" : "sm:hidden"}>New</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MattersControlsBar;
